import { useMemo, useState } from "react";
import { Pressable, ScrollView } from "react-native";
import { Text, View } from "@src/components";
import { sizes } from "@src/constants/sizes";
// @ts-expect-error baseline 2026-02-16
import { ReportsSection, ReportsListItem, ReportsListItemText } from "@src/components/web";
import { isWeb } from "@src/utils/web-helpers";
import { listWasteSenseFixtures, RANDOM_FIXTURE_ID } from "@src/features/wastesense/api";
import { generateSeedToken } from "@src/features/wastesense/random/prng";
import { useUnistyles } from "react-native-unistyles";

/**
 * How many completed weekly reports we ever offer in the picker.
 * One year of history feels like a sensible upper bound for the
 * prototype — long enough to demo "scroll back through history",
 * short enough that we don't produce a wall of mostly-empty rows.
 */
const WEEKLY_REPORTS_HARD_LIMIT = 52;

/**
 * How many weekly reports show on a single page. Tuned so the page
 * fits comfortably inside the existing reports panel without forcing
 * the user to scroll inside the section.
 */
const WEEKLY_REPORTS_PAGE_SIZE = 8;

/**
 * WasteSense report picker.
 *
 * Lists every available WasteSense fixture/report. Each row is rendered as a
 * real `<a href>` on web (via expo-router's `<Link asChild>`), which preserves
 * the in-app router behaviour for plain clicks while letting the browser
 * handle cmd/middle-click → new tab and right-click → "Open in new tab".
 * Once the API is live this will be backed by a tenant-scoped list endpoint
 * instead of the static fixture registry.
 *
 * The list also exposes a deterministic random fixture for surfacing
 * frontend edge cases — a fresh seed is minted each time the list mounts
 * (and on demand via "Re-roll") so the same user lands on different
 * scenarios across navigations rather than always hitting the same one.
 *
 * Above the fixtures we surface a placeholder list of recent weekly
 * reports (Mon–Sun, most-recent completed week first). The rows are
 * inert prototypes for now — they're here to validate the period-picker
 * UX before we wire up a real per-period reporting endpoint.
 */
export default function WasteSenseListScreen() {
  const fixtures = listWasteSenseFixtures();
  const [randomSeed, setRandomSeed] = useState<string>(() => generateSeedToken());

  // Compute the historical week list once per mount. Stable across
  // re-renders so pagination state doesn't get out from under itself
  // if the component re-renders mid-day.
  const weeklyReports = useMemo(() => buildWeeklyReports(new Date(), WEEKLY_REPORTS_HARD_LIMIT), []);
  const [weeksPage, setWeeksPage] = useState(0);

  if (!isWeb) {
    return (
      <View flex={1} alignItems="center" justifyContent="center" padding="3xl" gap="md">
        <Text textStyle="sm/semibold" color="text" textAlign="center">
          WasteSense reports are only available on the web client.
        </Text>
        <Text textStyle="xs/regular" color="textMuted" textAlign="center">
          Open this page in your browser to view your reports.
        </Text>
      </View>
    );
  }

  const randomHref = `/reports/wastesense/report?fixture=${RANDOM_FIXTURE_ID}&seed=${encodeURIComponent(randomSeed)}`;

  const totalPages = Math.max(1, Math.ceil(weeklyReports.length / WEEKLY_REPORTS_PAGE_SIZE));
  const pageStart = weeksPage * WEEKLY_REPORTS_PAGE_SIZE;
  const visibleWeeks = weeklyReports.slice(pageStart, pageStart + WEEKLY_REPORTS_PAGE_SIZE);
  const canGoNewer = weeksPage > 0;
  const canGoOlder = weeksPage < totalPages - 1;

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{
        paddingTop: 40,
        paddingBottom: 40,
        paddingHorizontal: 80,
        maxWidth: sizes.webContentMaxWidth,
      }}
      showsVerticalScrollIndicator={false}
    >
      <View gap="6xl">
        <View gap="sm" px="md">
          <Text textStyle="lg/semibold" color="text">
            WasteSense
          </Text>
          <Text textStyle="sm/regular" color="textMuted">
            A digest of illegal dumping and site activity.
          </Text>
          <Text textStyle="sm/regular" color="textMuted">
            Provides a clear answer to: &ldquo;Did anything happen on our site?&rdquo;
          </Text>
        </View>

        <ReportsSection title="Weekly reports">
          <View gap="md">
            {visibleWeeks.map((week) => (
              <ReportsListItem
                key={week.id}
                title={week.title}
                onPress={() => {
                  /*
                   * Placeholder. The picker shape is the prototype here;
                   * we'll wire each row to a per-period report endpoint
                   * once that's available.
                   */
                }}
              >
                <ReportsListItemText>{week.subtitle}</ReportsListItemText>
              </ReportsListItem>
            ))}
            {totalPages > 1 ?
              <PageControls
                page={weeksPage}
                totalPages={totalPages}
                canGoNewer={canGoNewer}
                canGoOlder={canGoOlder}
                onNewer={() => setWeeksPage((p) => Math.max(0, p - 1))}
                onOlder={() => setWeeksPage((p) => Math.min(totalPages - 1, p + 1))}
              />
            : null}
          </View>
        </ReportsSection>

        <ReportsSection title="Fixtures">
          {fixtures.map((entry) => (
            <ReportsListItem
              key={entry.fixture}
              title={entry.tenantName}
              href={`/reports/wastesense/report?fixture=${encodeURIComponent(entry.fixture)}`}
            />
          ))}
        </ReportsSection>

        <ReportsSection title="Testing">
          <View gap="md">
            <ReportsListItem title="Random WasteSense report" href={randomHref}>
              <ReportsListItemText>Deterministically generated from a seed.</ReportsListItemText>
            </ReportsListItem>
            {/* Sibling — not a child of the `<a>` — so the re-roll click can't
                bubble up to the link and immediately navigate away. */}
            <RandomSeedRow seed={randomSeed} onReroll={() => setRandomSeed(generateSeedToken())} />
          </View>
        </ReportsSection>
      </View>
    </ScrollView>
  );
}

interface WeeklyReportEntry {
  /** Stable identifier for React keys / future deep-linking. */
  id: string;
  /** Headline label, e.g. "Mon 26 May – Sun 1 Jun 2026". */
  title: string;
  /** Sub-label clarifying recency, e.g. "Most recent" or "3 weeks ago". */
  subtitle: string;
}

/**
 * Builds a list of completed Monday–Sunday weeks, most-recent first,
 * up to `limit` entries. "Completed" means the Sunday is strictly
 * before `today`, so a partially-elapsed current week never appears
 * (an incomplete period would mislead the reader).
 *
 * Times are computed in the viewer's local timezone. Per-tenant
 * timezones get plumbed in once we wire the real endpoint; for the
 * prototype, the picker just needs the labels to be obviously weekly.
 */
function buildWeeklyReports(now: Date, limit: number): readonly WeeklyReportEntry[] {
  const lastSunday = mostRecentSundayBefore(now);
  const out: WeeklyReportEntry[] = [];
  for (let i = 0; i < limit; i += 1) {
    const sunday = addDays(lastSunday, -7 * i);
    const monday = addDays(sunday, -6);
    out.push({
      id: `${formatIsoDate(monday)}_${formatIsoDate(sunday)}`,
      title: formatWeekRange(monday, sunday),
      subtitle: describeWeekRecency(i),
    });
  }
  return out;
}

/**
 * Returns the Sunday at the end of the most recent *completed* week
 * relative to `now`. If `now` is itself a Sunday, the current week is
 * still considered in-progress until midnight, so we step back a full
 * week — the picker should never offer a half-elapsed period.
 */
function mostRecentSundayBefore(now: Date): Date {
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // JS getDay(): Sunday = 0, Monday = 1, …, Saturday = 6.
  // Days back from `startOfDay` to land on the previous Sunday:
  // - Sunday today → 7 (skip the in-progress week)
  // - Monday today → 1, Tuesday → 2, … Saturday → 6.
  const dow = startOfDay.getDay();
  const daysBack = dow === 0 ? 7 : dow;
  return addDays(startOfDay, -daysBack);
}

function addDays(d: Date, days: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  return out;
}

function formatIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const WEEKDAY_FMT = new Intl.DateTimeFormat("en-AU", { weekday: "short" });
const DAY_MONTH_FMT = new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "short" });
const DAY_MONTH_YEAR_FMT = new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "short", year: "numeric" });

/**
 * Formats a week range as "Mon 26 May – Sun 1 Jun 2026". The year
 * shows on the closing date only — repeating it on both ends is
 * noise when the week doesn't straddle a year boundary, and the
 * closing date carries the canonical year either way.
 */
function formatWeekRange(monday: Date, sunday: Date): string {
  const startWeekday = WEEKDAY_FMT.format(monday);
  const endWeekday = WEEKDAY_FMT.format(sunday);
  // Drop the year on the start unless the week straddles year boundaries.
  const sameYear = monday.getFullYear() === sunday.getFullYear();
  const startLabel = sameYear ? DAY_MONTH_FMT.format(monday) : DAY_MONTH_YEAR_FMT.format(monday);
  const endLabel = DAY_MONTH_YEAR_FMT.format(sunday);
  return `${startWeekday} ${startLabel} – ${endWeekday} ${endLabel}`;
}

function describeWeekRecency(index: number): string {
  if (index === 0) return "Most recent week";
  if (index === 1) return "1 week ago";
  return `${index} weeks ago`;
}

interface PageControlsProps {
  page: number;
  totalPages: number;
  canGoNewer: boolean;
  canGoOlder: boolean;
  onNewer: () => void;
  onOlder: () => void;
}

/**
 * Inline pager for the weekly-reports section. Older = next page
 * (further back in time); Newer = previous page (closer to today).
 * The labels read as time-direction first because the user is
 * thinking "I want last June's report", not "give me page 4".
 */
function PageControls({ page, totalPages, canGoNewer, canGoOlder, onNewer, onOlder }: PageControlsProps) {
  const { theme } = useUnistyles();
  return (
    <View flexDirection="row" alignItems="center" justifyContent="space-between" gap="md" px="xl" pt="sm">
      <PagerButton label="← Newer" disabled={!canGoNewer} onPress={onNewer} />
      <Text textStyle="xs/regular" color="textMuted" style={{ color: theme.colors.textMuted }}>
        Page {page + 1} of {totalPages}
      </Text>
      <PagerButton label="Older →" disabled={!canGoOlder} onPress={onOlder} />
    </View>
  );
}

interface PagerButtonProps {
  label: string;
  disabled: boolean;
  onPress: () => void;
}

function PagerButton({ label, disabled, onPress }: PagerButtonProps) {
  const { theme } = useUnistyles();
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityState={{ disabled }}
      style={({ pressed }) => ({
        paddingVertical: theme.spacing.sm,
        paddingHorizontal: theme.spacing.md,
        borderRadius: theme.spacing.sm,
        backgroundColor:
          disabled ? "transparent"
          : pressed ? theme.colors.muted
          : "transparent",
        borderWidth: 1,
        borderColor: theme.colors.borderCard,
        opacity: disabled ? 0.4 : 1,
        // Only the enabled state advertises the click affordance; disabled
        // falls back to the browser default so the row clearly reads as inert.
        ...(disabled ? null : { cursor: "pointer" as const }),
      })}
    >
      <Text textStyle="xs/semibold" color={disabled ? "textMuted" : "text"}>
        {label}
      </Text>
    </Pressable>
  );
}

interface RandomSeedRowProps {
  seed: string;
  onReroll: () => void;
}

function RandomSeedRow({ seed, onReroll }: RandomSeedRowProps) {
  const { theme } = useUnistyles();
  return (
    <View
      flexDirection="row"
      alignItems="center"
      gap="md"
      flexWrap="wrap"
      px="xl"
      style={{ marginTop: -theme.spacing.sm }}
    >
      <Text textStyle="xs/regular" color="textMuted">
        Seed:{" "}
        <Text textStyle="xs/semibold" color="text">
          {seed}
        </Text>
      </Text>
      <Pressable
        onPress={onReroll}
        accessibilityRole="button"
        accessibilityLabel="Generate a new seed"
        style={({ pressed }) => ({
          paddingVertical: theme.spacing.sm,
          paddingHorizontal: theme.spacing.md,
          borderRadius: theme.spacing.sm,
          backgroundColor: pressed ? theme.colors.muted : "transparent",
          borderWidth: 1,
          borderColor: theme.colors.borderCard,
          cursor: "pointer",
        })}
      >
        <Text textStyle="xs/semibold" color="text">
          Re-roll
        </Text>
      </Pressable>
    </View>
  );
}
